if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          style: this.querySelector('[id^="GalleryStyle"]'),
        };
        this.progress = this.querySelector('.pw-pdp-progress');
        this.progressTrack = this.elements.viewer.querySelector('[id^="Slider-Gallery-"]');
        if (this.progress && this.progressTrack) {
          this.progressTrack.addEventListener('scroll', () => this.updateProgress(), { passive: true });
          this.progressObserver = new MutationObserver(() => this.updateProgress());
          this.progressObserver.observe(this.progressTrack, { childList: true });
          this.updateProgress();
          window.addEventListener('resize', () => this.updateProgress());
        }
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        const mediaObserver = new IntersectionObserver(this.onMediaChanged.bind(this), {
          threshold: 0.75,
        });

        this.appendChild(this.elements.style);

        this.fixMediaStyle = document.createElement('style');
        this.appendChild(this.fixMediaStyle);

        // NOTE: Expects the viewer to have at least one media item
        this.fixMedia(this.elements.viewer.querySelector('[data-media-id]').dataset.color);

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((media) => mediaObserver.observe(media));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      updateProgress() {
        if (!this.progress || !this.progressTrack) return;
        const slides = [...this.progressTrack.querySelectorAll('[data-media-id]')].filter(slide => slide.offsetWidth > 0);
        const trackRect = this.progressTrack.getBoundingClientRect();
        let active = 0, distance = Infinity;
        slides.forEach((slide, index) => {
          const current = Math.abs(slide.getBoundingClientRect().left - trackRect.left);
          if (current < distance) { distance = current; active = index; }
        });
        this.progress.style.setProperty('--pw-pdp-count', Math.max(slides.length, 1));
        this.progress.style.setProperty('--pw-pdp-index', active);
        this.progress.hidden = slides.length < 2;
      }

      fixMedia(color) {
        this.fixMediaStyle.textContent = ''; // Reset the style sheet to avoid conflicts with the new media
        const slides = [
          ...this.elements.viewer.slider.querySelectorAll(
            `[id^="Slide"][data-color='${color}']:not([data-featured]), [id^="Slide"][data-media-type='video']`
          ),
        ];
        let styles = '';
        if (slides.length === 0) {
          this.fixMediaStyle.textContent = styles;
          return;
        }
        if (window.matchMedia('(min-width: 990px)').matches) {
          const minWidth = slides
            .map((slide) => slide.querySelector('.product-media-container').clientWidth)
            .reduce((a, b) => Math.min(a, b));
          styles = `.product-media-container.constrain-height.media-fit-contain { --contained-width: ${minWidth}px !important; }`;
        }
        this.fixMediaStyle.textContent = styles;
      }

      onMediaChanged(entries) {
        entries.filter(entry => !entry.isIntersecting).forEach(entry => {
          entry.target.querySelectorAll('video').forEach(video => video.pause());
        });
        const visibleEntries = entries.filter(entry => entry.isIntersecting && entry.intersectionRatio >= 0.75);
        if (!visibleEntries.length) return;
        visibleEntries.sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const activeMedia = visibleEntries[0].target;
        this.playActiveMedia(activeMedia);

        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${activeMedia.dataset.mediaId}"]`
        );
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        activeThumbnail?.querySelector('button').setAttribute('aria-current', true);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            // Used on mobile. It scrolls to the first media after the color change.
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          this.elements.viewer.scrollTo({ top: activeMedia.offsetTop, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      hideNotSelectedColorMedia(color) {
        // Some products aren't organized by color (e.g. a "Value" option on gift cards),
        // so their media carry no matching data-color. In that case skip color-based
        // filtering entirely — otherwise every image would be hidden and fixMedia would
        // run reduce() over an empty slide list and throw, aborting the variant switch.
        const hasColorMedia = this.elements.viewer.querySelector(
          `[id^='Slide-'][data-media-type='image'][data-color='${color}']`
        );
        if (!hasColorMedia) return;

        this.elements.style.textContent = `[id^='Slide-'][data-media-type='image']:not([data-color='${color}']) { display: none; }`;

        this.fixMedia(color);
        this.updateProgress();

        this.elements.viewer.resetPages();
        if (this.elements.thumbnails) this.elements.thumbnails.resetPages();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        const bounds = activeItem.getBoundingClientRect();
        if (!activeItem.offsetWidth || bounds.bottom <= 0 || bounds.top >= window.innerHeight || bounds.right <= 0 || bounds.left >= window.innerWidth) return;
        window.pauseAllMedia();
        const inlineVideo = activeItem.querySelector('pw-inline-product-video');
        if (inlineVideo) {
          inlineVideo.prepare?.();
          if (!inlineVideo.userPaused) inlineVideo.video?.play().catch(() => {});
          return;
        }
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
        const video = activeItem.querySelector('video[controls]');
        if (video) video.play().catch(() => {});
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
